import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  AuthState, 
  AuthContextType, 
  RequestTokenResponse, 
  VerifyTokenResponse,
  DirectAuthResponse
} from '../types/auth.types';
import { authService } from '../services/authService';

// État initial de l'authentification
const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null
};

// Types d'actions pour le reducer
type AuthAction = 
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AuthState['user']; token: string } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_RESET_ERROR' };

// Reducer pour gérer les actions d'authentification
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        isLoading: false
      };
    case 'AUTH_RESET_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Création du contexte d'authentification
export const AuthContext = createContext<AuthContextType>({
  ...initialState,
  requestToken: async () => ({ status: '', message: '', data: { phone: '', expires_at: '' } }),
  verifyToken: async () => ({ status: '', message: '', data: undefined }),
  directAuth: async () => null,
  logout: () => {}
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Vérification de l'authentification au chargement
  useEffect(() => {
    const checkAuth = async () => {
      const token = authService.getToken();
      
      if (token && authService.isAuthenticated()) {
        try {
          // Ici, on pourrait faire une requête à l'API pour vérifier la validité du token
          // et récupérer les informations utilisateur à jour
          // Pour l'instant, on extrait simplement les informations du token
          const payload = JSON.parse(atob(token.split('.')[1]));
          
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: {
                id: payload.sub,
                phone: '', // On n'a pas cette info dans le token
                created_at: '',
                updated_at: ''
              },
              token
            }
          });
        } catch (error) {
          authService.removeToken();
          dispatch({ type: 'AUTH_LOGOUT' });
        }
      } else {
        authService.removeToken();
        dispatch({ type: 'AUTH_LOGOUT' });
      }
    };

    checkAuth();
  }, []);

  // Fonction pour demander un token d'authentification
  const requestToken = async (phone: string): Promise<RequestTokenResponse> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Validation du numéro de téléphone
      if (!authService.validatePhoneNumber(phone)) {
        dispatch({ 
          type: 'AUTH_ERROR', 
          payload: 'Numéro de téléphone invalide. Utilisez un format camerounais valide (ex: 6XXXXXXXX, 7XXXXXXXX, 8XXXXXXXX, 9XXXXXXXX)' 
        });
        throw new Error('Numéro de téléphone invalide');
      }
      
      // Formatage du numéro de téléphone
      const formattedPhone = authService.formatPhoneNumber(phone);
      
      // Appel à l'API pour demander un token
      const response = await authService.requestToken({ phone: formattedPhone });
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        dispatch({ type: 'AUTH_ERROR', payload: error.message });
        throw error;
      }
      const errorMsg = 'Une erreur est survenue lors de la demande de token';
      dispatch({ type: 'AUTH_ERROR', payload: errorMsg });
      throw new Error(errorMsg);
    }
  };

  // Fonction pour vérifier un token d'authentification
  const verifyToken = async (token: string): Promise<VerifyTokenResponse> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Appel à l'API pour vérifier le token
      const response = await authService.verifyToken({ token });
      
      if (response.status === 'success' && response.data) {
        // Stockage du JWT
        authService.setToken(response.data.token);
        
        // Mise à jour de l'état
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.data.user,
            token: response.data.token
          }
        });
      }
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        dispatch({ type: 'AUTH_ERROR', payload: error.message });
        throw error;
      }
      const errorMsg = 'Une erreur est survenue lors de la vérification du token';
      dispatch({ type: 'AUTH_ERROR', payload: errorMsg });
      throw new Error(errorMsg);
    }
  };

  // Fonction pour l'authentification directe
  const directAuth = async (phone: string): Promise<DirectAuthResponse | null> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      // Validation du numéro de téléphone
      if (!authService.validatePhoneNumber(phone)) {
        dispatch({ 
          type: 'AUTH_ERROR', 
          payload: 'Numéro de téléphone invalide. Utilisez un format camerounais valide (ex: 6XXXXXXXX, 7XXXXXXXX, 8XXXXXXXX, 9XXXXXXXX)' 
        });
        return null;
      }
      
      // Formatage du numéro de téléphone
      const formattedPhone = authService.formatPhoneNumber(phone);
      
      // Appel à l'API pour l'authentification directe
      const response = await authService.directAuth({ phone: formattedPhone });
      
      if (response.status === 'success' && response.data) {
        // Stockage du JWT
        authService.setToken(response.data.token);
        
        // Mise à jour de l'état
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: response.data.user,
            token: response.data.token
          }
        });
      } else {
        dispatch({ type: 'AUTH_ERROR', payload: response.message });
      }
      
      return response;
    } catch (error) {
      if (error instanceof Error) {
        dispatch({ type: 'AUTH_ERROR', payload: error.message });
      } else {
        dispatch({ type: 'AUTH_ERROR', payload: 'Une erreur est survenue lors de l\'authentification directe' });
      }
      return null;
    }
  };

  // Fonction pour se déconnecter
  const logout = () => {
    authService.removeToken();
    dispatch({ type: 'AUTH_LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        requestToken,
        verifyToken,
        directAuth,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
